/**
 * Tron deposit service — TronLink only.
 *
 * Mirrors the shape of `solanaInternullService` (chain-specific deposit
 * helper) but uses TronWeb. Native TRX only for v1.
 *
 * Chain-isolation invariant: this module accepts a `tronWeb` instance from
 * the caller and never touches `window.ethereum` or `window.solana`. The
 * caller is responsible for pulling the right tronWeb from `useTronWallet`.
 */

const NATIVE_TRX_TOKEN = '0x0000000000000000000000000000000000000000';

/**
 * Deposit native TRX to the treasury contract.
 *
 * Calls `deposit(address token, uint256 amount)` on MultiTokenTreasuryTron
 * with `callValue = amountSun`. Caller passes the tronWeb instance from
 * `useTronWallet()` and the treasury address from the chain config.
 *
 * @param {object} tronWeb - the TronWeb instance from useTronWallet
 * @param {string} treasuryAddress - T... base58 treasury address
 * @param {string|number} amountTrx - human-readable amount (e.g. 5 for 5 TRX)
 * @returns {Promise<{success: boolean, txHash: string, depositor: string}>}
 */
export async function depositNative(tronWeb, treasuryAddress, amountTrx) {
  if (!tronWeb) throw new Error('TronLink not connected');
  const sender = tronWeb.defaultAddress?.base58;
  if (!sender) throw new Error('TronLink has no active account');
  if (!treasuryAddress) throw new Error('No treasury address for Tron chain');

  const amountSun = Math.floor(parseFloat(amountTrx) * 1_000_000);
  if (!Number.isFinite(amountSun) || amountSun <= 0) {
    throw new Error(`Invalid TRX amount: ${amountTrx}`);
  }

  // Build deposit(address,uint256). For native TRX the token argument is
  // address(0); the actual TRX is attached via callValue. Same contract
  // signature as the EVM treasury.
  const built = await tronWeb.transactionBuilder.triggerSmartContract(
    treasuryAddress,
    'deposit(address,uint256)',
    {
      feeLimit: 100_000_000,   // 100 TRX energy ceiling — way more than needed
      callValue: amountSun,    // attach the TRX being deposited
    },
    [
      { type: 'address', value: NATIVE_TRX_TOKEN },
      { type: 'uint256', value: amountSun.toString() },
    ],
    sender,
  );

  if (!built?.result?.result) {
    throw new Error('Tron triggerSmartContract failed: ' + JSON.stringify(built?.result));
  }

  const signed = await tronWeb.trx.sign(built.transaction);
  const broadcast = await tronWeb.trx.sendRawTransaction(signed);
  if (!broadcast?.result) {
    throw new Error('TronGrid broadcast rejected: ' + JSON.stringify(broadcast));
  }

  const txHash = signed.txID || broadcast.txid || broadcast.txID;
  if (!txHash) throw new Error('No tx hash returned from broadcast');

  return { success: true, txHash, depositor: sender };
}

/**
 * Deposit a TRC-20 token (e.g. USDT-Nile) to the treasury contract.
 *
 * Flow:
 *   1. Read the user's allowance for the treasury. If it already covers the
 *      deposit, skip approve.
 *   2. Otherwise call `approve(treasury, amount)` on the token contract and
 *      wait for the approve tx to confirm.
 *   3. Call `deposit(token, amount)` on the treasury (no callValue — the
 *      treasury pulls the tokens via transferFrom).
 *
 * Amount is human-readable (e.g. 5 for 5 USDT); we scale by `decimals` to
 * reach the base-unit value the contract expects.
 *
 * @param {object} params
 * @param {object} params.tronWeb - TronWeb instance from useTronWallet
 * @param {string} params.treasuryAddress - Treasury T... base58
 * @param {string} params.tokenAddress - TRC-20 contract T... base58
 * @param {string|number} params.amount - human-readable amount
 * @param {number} params.decimals - token decimals (USDT = 6)
 * @returns {Promise<{success: boolean, txHash: string, depositor: string}>}
 */
export async function depositToken({ tronWeb, treasuryAddress, tokenAddress, amount, decimals }) {
  if (!tronWeb) throw new Error('TronLink not connected');
  const sender = tronWeb.defaultAddress?.base58;
  if (!sender) throw new Error('TronLink has no active account');
  if (!treasuryAddress) throw new Error('No treasury address for Tron chain');
  if (!tokenAddress) throw new Error('No TRC-20 token address provided');
  if (decimals === undefined || decimals === null) throw new Error('Token decimals missing');

  // Compute base-unit amount as BigInt to avoid float drift on small decimals.
  // amount may be "5" or 5 — split on "." so we don't lose precision via Number().
  const parts = String(amount).split('.');
  const whole = BigInt(parts[0] || '0');
  const frac = (parts[1] || '').padEnd(decimals, '0').slice(0, decimals);
  const fracBig = BigInt(frac || '0');
  const scale = 10n ** BigInt(decimals);
  const amountBase = whole * scale + fracBig;
  if (amountBase <= 0n) throw new Error(`Invalid amount: ${amount}`);
  const amountBaseStr = amountBase.toString();

  // 1. Allowance check via triggerconstantcontract (no signature, no fee).
  let allowance = 0n;
  try {
    const tokenContract = await tronWeb.contract().at(tokenAddress);
    const raw = await tokenContract.allowance(sender, treasuryAddress).call();
    allowance = BigInt(raw?.toString?.() ?? raw ?? '0');
  } catch (e) {
    console.warn('allowance() read failed, will request approval anyway:', e?.message || e);
    allowance = 0n;
  }

  // 2. Approve if needed. We approve exactly the deposit amount (not max-uint)
  //    for safety; users can always re-approve on the next deposit.
  if (allowance < amountBase) {
    const builtApprove = await tronWeb.transactionBuilder.triggerSmartContract(
      tokenAddress,
      'approve(address,uint256)',
      { feeLimit: 100_000_000 },
      [
        { type: 'address', value: treasuryAddress },
        { type: 'uint256', value: amountBaseStr },
      ],
      sender,
    );
    if (!builtApprove?.result?.result) {
      throw new Error('approve() trigger failed: ' + JSON.stringify(builtApprove?.result));
    }
    const signedApprove = await tronWeb.trx.sign(builtApprove.transaction);
    const broadcastApprove = await tronWeb.trx.sendRawTransaction(signedApprove);
    if (!broadcastApprove?.result) {
      throw new Error('approve() broadcast rejected: ' + JSON.stringify(broadcastApprove));
    }
    const approveTxId = signedApprove.txID || broadcastApprove.txid || broadcastApprove.txID;

    // Wait for approve to be included in a block before submitting the deposit;
    // otherwise transferFrom in the deposit tx will see the old allowance.
    // Tron blocks are ~3s — poll for up to 30s.
    const deadline = Date.now() + 30_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const info = await tronWeb.trx.getTransactionInfo(approveTxId);
        if (info && info.id && info.blockNumber) break;
      } catch (_) { /* keep polling */ }
    }
  }

  // 3. Deposit: call treasury.deposit(token, amount). No callValue for TRC-20.
  const built = await tronWeb.transactionBuilder.triggerSmartContract(
    treasuryAddress,
    'deposit(address,uint256)',
    { feeLimit: 200_000_000, callValue: 0 },
    [
      { type: 'address', value: tokenAddress },
      { type: 'uint256', value: amountBaseStr },
    ],
    sender,
  );
  if (!built?.result?.result) {
    throw new Error('deposit() trigger failed: ' + JSON.stringify(built?.result));
  }
  const signed = await tronWeb.trx.sign(built.transaction);
  const broadcast = await tronWeb.trx.sendRawTransaction(signed);
  if (!broadcast?.result) {
    throw new Error('deposit() broadcast rejected: ' + JSON.stringify(broadcast));
  }

  const txHash = signed.txID || broadcast.txid || broadcast.txID;
  if (!txHash) throw new Error('No tx hash returned from deposit broadcast');

  return { success: true, txHash, depositor: sender };
}

/**
 * Build an explorer URL for a Tron tx. Network is inferred from the chain
 * name in our config (`tron-nile` → Nile testnet explorer, etc.).
 */
export function explorerUrl(network, txHash) {
  if (!txHash) return '';
  if (network === 'tron-mainnet' || network === 'tron') {
    return `https://tronscan.org/#/transaction/${txHash}`;
  }
  // Default to Nile for any non-mainnet config.
  return `https://nile.tronscan.org/#/transaction/${txHash}`;
}

const tronInternullService = { depositNative, depositToken, explorerUrl };
export default tronInternullService;
